import React, { useState, useRef, useCallback } from 'react';
import { ScrollText, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ClinicOnboardingData } from '../types';

interface AcknowledgementStepProps {
  data: ClinicOnboardingData;
  updateData: (updates: Partial<ClinicOnboardingData>) => void;
  onAcknowledged: (acknowledged: boolean) => void;
}

const GUIDED_TERMS = `NIMBUS PROVIDERS — GUIDED CLINIC PROGRAM AGREEMENT

This Program Acknowledgement ("Agreement") governs the relationship between the undersigned clinic ("Clinic") and Nimbus Healthcare, Inc. ("Nimbus") for participation in the Nimbus Providers Guided Clinic Program.

1. PROGRAM STRUCTURE

The Clinic participates as a Guided clinic on the Nimbus Providers platform. Under this model:

  a. The Clinic does not employ or contract with prescribers for the purpose of Nimbus program orders. All prescribing decisions are made independently by Nimbus-affiliated licensed healthcare providers ("Nimbus Providers").

  b. The Clinic's role is limited to patient education, program presentation, enrollment facilitation, and follow-up coordination — collectively referred to as "Facilitation Services."

  c. Patients select from program options at published program prices. The Clinic does not recommend, suggest, or direct specific medications or treatment protocols.

2. COMPENSATION — FACILITATION FEE

  a. The Clinic receives a fixed Facilitation Fee for each patient enrollment. This fee is set at a fixed dollar amount per program option and does not vary based on which medication or treatment the Nimbus Provider prescribes.

  b. The Facilitation Fee compensates the Clinic solely for legitimate Facilitation Services rendered at fair market value.

  c. The Facilitation Fee is automatically transferred to the Clinic's connected Stripe account via destination charges upon successful payment capture.

3. PAYMENT TIMING

  a. Patient payments are authorized (held) at the time of program selection but are NOT captured until a Nimbus Provider independently reviews the patient's enrollment and verifies a prescription.

  b. If a Nimbus Provider does not prescribe or the patient does not complete enrollment within the applicable time window, the payment hold expires automatically and no charge is made to the patient.

  c. The Clinic receives its Facilitation Fee only after a prescription has been verified and payment has been captured. No Facilitation Fee is earned if no prescription is issued.

4. ANTI-KICKBACK COMPLIANCE

  a. The Clinic acknowledges that the Facilitation Fee is a fixed amount that does not vary based on patient volume, prescription content, medication selection, or referral patterns.

  b. The Clinic shall not recommend, suggest, or influence the selection of specific medications, dosages, or treatment protocols for any Nimbus-routed program order.

  c. The Clinic acknowledges that Nimbus Providers exercise independent clinical judgment and the Clinic has no authority or influence over prescribing decisions.

5. CORPORATE PRACTICE OF MEDICINE (CPOM) COMPLIANCE

  a. The Clinic acknowledges that it does not engage in the practice of medicine through its participation in this program. Medical decisions are made exclusively by independently licensed Nimbus Providers.

  b. The economic structure of this program reflects this separation: the Clinic is compensated for facilitation services, and Nimbus retains the program delivery fee to cover clinical services, pharmacy fulfillment, and care coordination.

6. CLINIC RESPONSIBILITIES

  a. Present Nimbus program options accurately and without modification to patients.
  b. Facilitate patient enrollment form completion in a timely manner.
  c. Not represent to patients that the Clinic provides medical consultations, prescriptions, or clinical oversight for Nimbus program orders.
  d. Maintain current business licenses and insurance as required by applicable state law.
  e. Report any compliance concerns to Nimbus promptly.

7. TERM

This Agreement is effective upon acknowledgement during onboarding and remains in effect for as long as the Clinic maintains active status on the Nimbus Providers platform.

By clicking "I Acknowledge & Agree" below, the undersigned authorized representative of the Clinic confirms that they have read, understood, and agree to all terms outlined above.`;

const DIRECT_TERMS = `NIMBUS PROVIDERS — DIRECT CLINIC PLATFORM AGREEMENT

This Platform Acknowledgement ("Agreement") governs the relationship between the undersigned clinic ("Clinic") and Nimbus Healthcare, Inc. ("Nimbus") for participation on the Nimbus Providers platform.

1. PROGRAM STRUCTURE

The Clinic participates as a Direct clinic on the Nimbus Providers platform. Under this model:

  a. The Clinic employs or contracts with its own licensed prescribers who independently make all clinical and prescribing decisions for patient orders placed through the platform.

  b. Nimbus provides the technology platform, pharmacy fulfillment infrastructure, and operational support services.

  c. The Clinic's prescribers select medications, configure dosages, and authorize prescriptions directly through the platform.

2. PLATFORM SERVICES

Nimbus provides the following services to the Clinic:

  a. Staff-facing storefront for medication selection and order placement.
  b. Integrated payment processing via Stripe Connect.
  c. Prescription routing to Lake Hills Pharmacy (or designated fulfillment pharmacy) for compounding and dispensing.
  d. Patient intake form management and documentation.
  e. Shipping and delivery coordination.

3. PRICING & FEES

  a. The Clinic purchases medications at clinic cost and sets patient-facing prices. The margin between clinic cost and patient price is retained by the Clinic.

  b. A fixed platform fee is applied per transaction as disclosed in the Clinic's service agreement. This fee does not vary based on which medications are prescribed.

4. CLINIC RESPONSIBILITIES

  a. Ensure all prescriptions are written by appropriately licensed prescribers with valid state licenses and DEA registrations.
  b. Maintain compliance with all applicable federal and state regulations regarding prescribing and dispensing.
  c. Not permit unlicensed staff to make prescribing decisions or authorize prescriptions on the platform.
  d. Maintain current business licenses and insurance as required by applicable state law.

5. TERM

This Agreement is effective upon acknowledgement during onboarding and remains in effect for as long as the Clinic maintains active status on the Nimbus Providers platform.

By clicking "I Acknowledge & Agree" below, the undersigned authorized representative of the Clinic confirms that they have read, understood, and agree to all terms outlined above.`;

const HYBRID_TERMS = `NIMBUS PROVIDERS — HYBRID CLINIC PROGRAM AGREEMENT

This Program Acknowledgement ("Agreement") governs the relationship between the undersigned clinic ("Clinic") and Nimbus Healthcare, Inc. ("Nimbus") for participation in the Nimbus Providers Hybrid Clinic Program.

1. PROGRAM STRUCTURE

The Clinic participates as a Hybrid clinic on the Nimbus Providers platform. Under this model, the Clinic operates two distinct program types simultaneously:

  DIRECT PROGRAMS (In-House Prescriber)
  a. For programs designated as "Direct," the Clinic's own licensed prescribers independently make all clinical and prescribing decisions.
  b. These programs use medication-level pricing, standard payment capture, and the Clinic retains the margin between clinic cost and patient price.

  GUIDED PROGRAMS (Nimbus-Routed)
  a. For programs designated as "Guided," all prescribing decisions are made independently by Nimbus-affiliated licensed healthcare providers ("Nimbus Providers").
  b. The Clinic's role for Guided programs is limited to Facilitation Services: patient education, program presentation, enrollment facilitation, and follow-up coordination.
  c. The Clinic receives a fixed Facilitation Fee for Guided program enrollments that does not vary based on medication selection.

2. COMPENSATION

  a. Direct Programs: The Clinic sets patient-facing prices and retains the margin above clinic cost. A fixed platform fee applies per transaction.

  b. Guided Programs: The Clinic receives a fixed Facilitation Fee per enrollment. Nimbus retains the program delivery fee. Payment is held until a Nimbus Provider verifies a prescription; if no prescription is issued, no charge is made and no Facilitation Fee is earned.

3. COMPLIANCE

  a. The Clinic shall not recommend specific medications or influence prescribing decisions for Guided program orders.
  b. The Clinic acknowledges that Facilitation Fees for Guided programs are fixed amounts at fair market value, with no volume-based incentives.
  c. For Direct programs, the Clinic is responsible for ensuring all prescriptions are written by appropriately licensed prescribers.
  d. The Clinic acknowledges the economic and operational separation between Direct and Guided programs and shall not commingle the two models.

4. CLINIC RESPONSIBILITIES

  a. Clearly designate each program as Direct or Guided during setup.
  b. Not represent to patients that the Clinic provides medical consultations for Nimbus-routed (Guided) program orders.
  c. Maintain current business licenses, prescriber licenses, and insurance as required by applicable state law.
  d. Report any compliance concerns to Nimbus promptly.

5. TERM

This Agreement is effective upon acknowledgement during onboarding and remains in effect for as long as the Clinic maintains active status on the Nimbus Providers platform.

By clicking "I Acknowledge & Agree" below, the undersigned authorized representative of the Clinic confirms that they have read, understood, and agree to all terms outlined above.`;

function getTermsForArchetype(archetype: string | undefined): string {
  switch (archetype) {
    case 'guided':
      return GUIDED_TERMS;
    case 'hybrid':
      return HYBRID_TERMS;
    case 'direct':
    default:
      return DIRECT_TERMS;
  }
}

function getTitleForArchetype(archetype: string | undefined): string {
  switch (archetype) {
    case 'guided':
      return 'Guided Clinic Program Agreement';
    case 'hybrid':
      return 'Hybrid Clinic Program Agreement';
    case 'direct':
    default:
      return 'Direct Clinic Platform Agreement';
  }
}

export const AcknowledgementStep: React.FC<AcknowledgementStepProps> = ({
  data,
  updateData,
  onAcknowledged,
}) => {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [acknowledged, setAcknowledged] = useState(!!data.programAcknowledged);
  const scrollRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 40;
    if (atBottom && !hasScrolledToBottom) {
      setHasScrolledToBottom(true);
    }
  }, [hasScrolledToBottom]);

  const handleAcknowledge = () => {
    setAcknowledged(true);
    updateData({ programAcknowledged: true });
    onAcknowledged(true);
  };

  const terms = getTermsForArchetype(data.clinicArchetype);
  const title = getTitleForArchetype(data.clinicArchetype);

  return (
    <div className="space-y-4">
      <h3 className="text-xs font-semibold uppercase text-brand-teal tracking-wider">
        Program Acknowledgement
      </h3>

      <div className="rounded-xl border border-gray-200 bg-white">
        <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-3">
          <ScrollText className="h-4 w-4 text-brand-teal" />
          <span className="text-sm font-medium text-gray-900">{title}</span>
        </div>

        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="h-[380px] overflow-y-auto px-5 py-4"
        >
          <pre className="whitespace-pre-wrap font-sans text-[13px] leading-relaxed text-gray-700">
            {terms}
          </pre>
        </div>

        <div className="border-t border-gray-100 px-4 py-3">
          {acknowledged ? (
            <div className="flex items-center gap-2 rounded-xl bg-brand-teal/10 border border-brand-teal/20 px-4 py-3">
              <CheckCircle className="h-5 w-5 text-brand-teal" />
              <span className="text-sm font-medium text-brand-teal">
                Acknowledged — you may proceed to review.
              </span>
            </div>
          ) : (
            <div className="space-y-2">
              {!hasScrolledToBottom && (
                <p className="text-xs text-gray-500 text-center">
                  Please scroll to the bottom of the document to continue.
                </p>
              )}
              <Button
                onClick={handleAcknowledge}
                disabled={!hasScrolledToBottom}
                className="w-full gap-2 h-10 rounded-full bg-brand-teal hover:bg-brand-teal/90 text-white disabled:opacity-40"
              >
                <CheckCircle className="h-4 w-4" />
                I Acknowledge &amp; Agree
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AcknowledgementStep;
